import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Tag, Bookmark, Wallet, User, Menu, ChevronDown, LogOut, Settings as SettingsIcon, Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Wordmark } from "@/components/Wordmark";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DEMO_MEMBER, signOutMember } from "@/lib/mock-data";
import { useStoreVersion } from "@/hooks/useStore";

const memberNav = [
  { to: "/member/dashboard",    label: "Dashboard",       icon: LayoutDashboard },
  { to: "/member/deals",        label: "Browse Deals",    icon: Tag },
  { to: "/member/reservations", label: "My Reservations", icon: Bookmark },
  { to: "/member/payments",     label: "Payment History", icon: Wallet },
  { to: "/member/account",      label: "Account",         icon: User },
];

export function MemberShell({ children, pageTitle, pageSub, headerActions }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useStoreVersion();
  const initials = `${DEMO_MEMBER.firstName?.[0] || "J"}${DEMO_MEMBER.lastName?.[0] || "W"}`;

  const sidebarBody = (
    <>
      <div className="px-5 py-5 border-b border-line">
        <Link to="/member/dashboard" className="flex items-center">
          <Wordmark size="sm" />
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {memberNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => cn("sidenav-row", isActive && "active")}
            onClick={() => setOpen(false)}
          >
            <item.icon className="size-4" strokeWidth={2} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-line">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-surface-alt">
              <Avatar className="size-8">
                <AvatarFallback className="bg-brand-700 text-white text-caption font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <div className="text-body-sm font-semibold text-ink truncate">
                  {DEMO_MEMBER.firstName} {DEMO_MEMBER.lastName}
                </div>
                <div className="text-caption text-ink-subtle truncate">{DEMO_MEMBER.email}</div>
              </div>
              <ChevronDown className="size-4 text-ink-hint" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
            <DropdownMenuItem disabled className="text-caption">
              {DEMO_MEMBER.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/member/account")}>
              <SettingsIcon className="size-4 mr-2" /> Account settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { signOutMember(); navigate("/login"); }} data-testid="sign-out-btn">
              <LogOut className="size-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-surface-alt">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-60 bg-surface border-r border-line flex-col z-30">
        {sidebarBody}
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[260px]">
          <div className="flex flex-col h-full">{sidebarBody}</div>
        </SheetContent>
      </Sheet>

      <div className="lg:ml-60">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-surface border-b border-line">
          <div className="px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button className="lg:hidden size-9 rounded-md hover:bg-surface-alt grid place-items-center" aria-label="Open navigation"><Menu className="size-5" /></button>
                </SheetTrigger>
              </Sheet>
              {pageTitle && (
                <div className="min-w-0">
                  <h1 className="text-h2 text-ink font-brand truncate">{pageTitle}</h1>
                  {pageSub && <p className="text-caption text-ink-subtle truncate">{pageSub}</p>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {headerActions}
              <ThemeToggle />
              <button className="size-9 rounded-md hover:bg-surface-alt grid place-items-center text-ink-muted" aria-label="Notifications">
                <Bell className="size-4" />
              </button>
            </div>
          </div>
        </div>

        <main className="px-6 lg:px-8 py-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default MemberShell;
